'use client';

import type { CSSProperties } from 'react';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import {
  noticeBannerTokens,
  type NoticeBannerVariant,
} from '@/shared/ui/NoticeBanner';
import type { VariantNudge } from '@/types/concept';

interface VariantNudgeCalloutProps {
  nudge: VariantNudge;
  /**
   * Switch the next render to Explore ("New design"). When omitted the callout
   * is purely informational (a `div`, not a `button`).
   */
  onActivate?: () => void;
  /**
   * Horizontal caret position as a fraction of the callout width. `0.25` aims it
   * under the LEFT "New design" segment (new_variant nudge); `0.5` centers it on
   * the whole toggle (choose_path nudge). Defaults to `0.25`.
   */
  caretFraction?: number;
  /**
   * Which edge carries the caret: `top` points UP at a toggle ABOVE the callout;
   * `bottom` points DOWN at a toggle BELOW it (used when the nudge renders above
   * the render-mode toggle). Defaults to `top`.
   */
  caretEdge?: 'top' | 'bottom';
  /**
   * Palette taken from the centralized `NoticeBanner` tokens. `warning` (amber)
   * is used for the `choose_path` nudge: the teal `info` palette is the brand
   * color used by almost every surface in the app, so an info-colored banner
   * reads as decoration and gets skipped — which is exactly what happened with
   * the old teal "two ways to keep going" callout. Defaults to `info`.
   */
  variant?: NoticeBannerVariant;
  /** Shows a ✕ in the top-end corner. Omitted = not closable. */
  onDismiss?: () => void;
  className?: string;
}

/**
 * Server-driven nudge callout shown next to the Explore/Refine toggle. It
 * mirrors the centralized `NoticeBanner` info variant (teal #D6E6E6 family) and
 * adds a caret on the edge facing the toggle — pointing down at a toggle below
 * it (`caretEdge="bottom"`, the concept-page arrangement) or up at one above it
 * (`"top"`) — see RenderModeToggle. Two server nudge types use it, BOTH tappable
 * and both pushing toward Explore:
 *  - `new_variant`: tapping selects Explore mode via `onActivate`.
 *  - `choose_path` (right after the first render): caret CENTERED
 *    (`caretFraction` 0.5) on the whole toggle, `warning` (amber) palette, and
 *    closable with a ✕ when the server marks it `dismissible`.
 *
 * Title + message come pre-localized from the API and are rendered AS-IS
 * (project rule: API-driven messages — no client-side copy).
 *
 * The callout stays visible until the server recomputes `variant_nudge` after
 * the user actually creates a new variant — selecting the mode alone does not
 * dismiss it.
 */
export function VariantNudgeCallout({
  nudge,
  onActivate,
  caretFraction = 0.25,
  caretEdge = 'top',
  variant = 'info',
  onDismiss,
  className,
}: VariantNudgeCalloutProps) {
  const t = useTranslations('common');
  const tokens = noticeBannerTokens[variant];
  // Same footprint cap/centering as the toggle below so the caret lines up under
  // the targeted segment.
  const wrapperClassName = cn(
    'relative w-full max-w-md mx-auto block text-start',
    className
  );

  const inner = (
    <>
      {/* Body — mirrors NoticeBanner info variant tokens exactly. Own uniform,
          smaller corner radius (rounded-lg, ~8px) so the message is clearly
          distinct in shape from the larger settings-group cards. The full inset
          ring outlines all four edges. */}
      <div
        className={cn(
          'relative p-4 rounded-lg',
          // Keep the text clear of the ✕.
          onDismiss && 'pe-10',
          tokens.bg,
          tokens.border,
          // Hover feedback only when the callout is actually tappable. Both
          // nudge types that use this component are tappable now; a plain
          // brightness nudge works for every palette without per-variant hover
          // colors.
          onActivate && 'transition-[filter] hover:brightness-[0.97]'
        )}
      >
        <div
          className={cn(
            'text-form-label leading-[1.15] font-semibold',
            tokens.title
          )}
        >
          {nudge.title}
        </div>
        <div
          className={cn('text-card-meta leading-[1.15] mt-1', tokens.message)}
        >
          {nudge.message}
        </div>
      </div>
      {/* Guiding caret on the edge facing the toggle. A rotated square with
          only two adjacent borders showing reads as a triangle whose tip points
          at the toggle; the teal fill matches the callout body so it looks like
          a speech-bubble tail. The `left` (driven by caretFraction) +
          -translate-x-1/2 centers the tip at that fraction of the shared
          max-w-md footprint — 25% sits over the left "New variant" (Explore)
          half, 50% centers it on the whole toggle (choose_path).

          Rendered AFTER the body so it stacks on top: half of the rotated
          square overlaps into the body and its solid teal fill MASKS the body's
          border ring exactly under the caret, while the caret's own two-sided
          inset border carries the outline outward. The result is a seamless
          join — no seam line, no partial line — where the caret grows out of
          the banner body (mirrors Apple). The 4px overlap (-top-1 / -bottom-1)
          keeps enough coverage to hide the seam. Top edge shows the top+left
          borders (tip up); bottom edge shows bottom+right (tip down). */}
      <span
        aria-hidden
        style={
          {
            left: `${caretFraction * 100}%`,
            // The caret needs the SAME ring color as the body but in a
            // two-sided shadow geometry, for which no literal Tailwind class
            // exists in the token file. Feeding the raw token colors through CSS
            // custom properties keeps the shadow classes static (Tailwind can
            // see them) while the palette still comes from noticeBannerTokens —
            // no per-variant caret colors duplicated here.
            '--caret-ring': tokens.ring,
            '--caret-ring-dark': tokens.ringDark,
          } as CSSProperties
        }
        className={cn(
          'absolute -translate-x-1/2 h-3 w-3 rotate-45 rounded-[2px]',
          tokens.bg,
          caretEdge === 'top'
            ? '-top-1 shadow-[inset_1px_1px_0_0_var(--caret-ring)] dark:shadow-[inset_1px_1px_0_0_var(--caret-ring-dark)]'
            : '-bottom-1 shadow-[inset_-1px_-1px_0_0_var(--caret-ring)] dark:shadow-[inset_-1px_-1px_0_0_var(--caret-ring-dark)]'
        )}
      />
    </>
  );

  const body = onActivate ? (
    <button type="button" onClick={onActivate} className={wrapperClassName}>
      {inner}
    </button>
  ) : (
    <div className={wrapperClassName}>{inner}</div>
  );

  if (!onDismiss) {
    return body;
  }

  // The ✕ is a sibling of the tappable body, not a child: a button inside a
  // button is invalid and the outer one would swallow the click. 32px hit area,
  // the 14px glyph lands 8px from the body corner.
  return (
    <div className="relative w-full max-w-md mx-auto">
      {body}
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('close')}
        className={cn(
          // The caret overhangs outside the body box, so the corner is the
          // wrapper corner on either edge.
          'absolute -top-px -end-px flex h-8 w-8 items-center justify-center',
          tokens.message
        )}
      >
        <X size={14} strokeWidth={2.5} aria-hidden />
      </button>
    </div>
  );
}
